import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MilkQualityPerFarmerPeriodData } from "../hooks/useInsightsDashboardQueries";
import {
  getSccZone,
  getTbcZone,
  SCC_THRESHOLDS,
  TBC_THRESHOLDS,
} from "../milkQualityThresholds";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

type ZoneCounts = { period: string; green: number; amber: number; red: number };

const ZONE_ORDER = ["green", "amber", "red"] as const;
type ZoneKey = typeof ZONE_ORDER[number];

function buildDistribution(
  data: MilkQualityPerFarmerPeriodData[],
  metric: "scc" | "tbc"
): ZoneCounts[] {
  const getZone = metric === "scc" ? getSccZone : getTbcZone;
  const key =
    metric === "scc"
      ? "MilkQuality.somaticCellCount"
      : "MilkQuality.totalBacterialCount";

  const byPeriod = new Map<
    string,
    { green: number; amber: number; red: number }
  >();
  const farmerSeen = new Map<string, Set<string>>();

  for (const r of data) {
    const period = String(r["MilkQuality.periodStart"]).split("T")[0];
    const uid = r["MilkQuality.userGuid"];

    if (!farmerSeen.has(period)) farmerSeen.set(period, new Set());
    const seenSet = farmerSeen.get(period);
    if (seenSet?.has(uid)) continue;
    seenSet?.add(uid);

    if (!byPeriod.has(period))
      byPeriod.set(period, { green: 0, amber: 0, red: 0 });
    const counts = byPeriod.get(period);
    if (!counts) continue;
    const val = Number(r[key]);
    if (isNaN(val)) continue;
    const zone = getZone(val);
    counts[zone]++;
  }

  return Array.from(byPeriod.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, counts]) => ({ period, ...counts }));
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

function formatMonth(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Brand-aligned zone fills — softer than the traffic-light defaults so the
// chart sits with the dahlia/daisy palette used across the app.
const ZONE_FILL: Record<ZoneKey, string> = {
  green: COLORS.dahlia[500], // #7EC254
  amber: COLORS.daisy[300], // #F7E880
  red: "#D98A8A", // muted brick red
};

const FILL_OPACITY = 0.8;
const BAR_RADIUS = 3;

/** SVG path for a rectangle with only its top two corners rounded. */
function topRoundedPath(
  x: number,
  y: number,
  width: number,
  height: number,
  r: number
): string {
  if (r <= 0) return `M${x},${y}h${width}v${height}h${-width}Z`;
  return `M${x},${y + r}a${r},${r} 0 0 1 ${r},${-r}h${
    width - 2 * r
  }a${r},${r} 0 0 1 ${r},${r}v${height - r}h${-width}Z`;
}

interface RoundedSegmentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: ZoneCounts;
  zone: ZoneKey;
}

/**
 * Stacked-bar segment that rounds its top corners only when it is the topmost
 * non-zero zone of its bar, so the stack reads as one column with a rounded cap.
 */
function RoundedSegment({
  x,
  y,
  width,
  height,
  fill,
  payload,
  zone,
}: RoundedSegmentProps) {
  if (x == null || y == null || width == null || !height) return null;
  const topZone = [...ZONE_ORDER]
    .reverse()
    .find((z) => (payload?.[z] ?? 0) > 0);
  const r = topZone === zone ? Math.min(BAR_RADIUS, width / 2, height) : 0;
  return (
    <path
      d={topRoundedPath(x, y, width, height, r)}
      fill={fill}
      fillOpacity={FILL_OPACITY}
    />
  );
}

function LegendItem({
  color,
  label,
  count,
}: {
  color: string;
  label: string;
  count?: number;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "3px",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography variant="caption" sx={{ color: "#52525b" }}>
        {label}
        {count != null && (
          <Box component="span" sx={{ fontWeight: 700, color: "#18181b" }}>
            {" "}
            · {count}
          </Box>
        )}
      </Typography>
    </Box>
  );
}

interface Props {
  title: string;
  data: MilkQualityPerFarmerPeriodData[] | undefined;
  metric: "scc" | "tbc";
  isLoading: boolean;
}

export default function MilkQualityDistributionChart({
  title,
  data,
  metric,
  isLoading,
}: Props) {
  const thresholds = metric === "scc" ? SCC_THRESHOLDS : TBC_THRESHOLDS;
  const unit = metric === "scc" ? "k cells/ml" : "k cfu/ml";

  const chartData = useMemo(
    () => (data ? buildDistribution(data, metric) : []),
    [data, metric]
  );

  const latest = chartData[chartData.length - 1];

  const zoneLabels: Record<ZoneKey, string> = {
    green: `Good (<${thresholds.good})`,
    amber: `Warning (${thresholds.good}–${thresholds.warning})`,
    red: `Critical (>${thresholds.warning})`,
  };

  if (isLoading) {
    return (
      <Card sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", mb: 2, display: "block" }}
      >
        Number of farmers by threshold zone ({unit}) over time
      </Typography>

      {/* Legend with latest-month counts */}
      {latest && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
          {ZONE_ORDER.map((zone) => (
            <LegendItem
              key={zone}
              color={ZONE_FILL[zone]}
              label={zoneLabels[zone]}
              count={latest[zone]}
            />
          ))}
        </Box>
      )}

      {/* Farmer counts by zone over time */}
      <Box sx={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            barCategoryGap="8%"
            margin={{ top: 5, right: 8, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxis
              dataKey="period"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={36}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              width={36}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              labelFormatter={(label) => formatMonth(String(label))}
              formatter={(value: number, name: string, props) => {
                const row = props.payload as ZoneCounts;
                const total = row.green + row.amber + row.red;
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return [
                  `${value} farmer${value === 1 ? "" : "s"} (${pct}%)`,
                  zoneLabels[name as ZoneKey] ?? name,
                ];
              }}
            />
            {ZONE_ORDER.map((zone) => (
              <Bar
                key={zone}
                dataKey={zone}
                stackId="zone"
                fill={ZONE_FILL[zone]}
                shape={<RoundedSegment zone={zone} />}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}
